import { Box, Button, Loader, Text } from "@wix/design-system";
import { useTranslation } from "@wix/fe-essentials-standalone";
import type { FC } from "react";
import { SiteThumbnail } from "../SiteThumbnail/SiteThumbnail";
import "./SitesList.css";
import { useSitesList } from "./useSitesList";
import { useMobile } from "../../../hooks/useMobile";

export const SitesList: FC = () => {
  const { sites, isLoading, isFetchingNextPage, hasMore, loadMore } =
    useSitesList();
  const { t } = useTranslation();
  const isMobile = useMobile();

  if (sites && sites.length > 0) {
    return (
      <Box
        direction="vertical"
        align="center"
        width={isMobile ? "calc(100% - 32px)" : "calc(100% - 96px)"}
        height="100%"
        marginTop={isMobile ? 0 : 10}
        padding={isMobile ? "0 16px" : "0 48px"}
      >
        <Box
          width="100%"
          align="left"
          maxWidth={1480}
          marginTop={2}
          paddingLeft={4}
          marginBottom={isMobile ? "0" : "16px"}
          className="site-list"
        >
          <Text
            className="site-list-title"
            size="medium"
            weight="normal"
            color="var(--color-text-icon-text-standard-primary, #131720)"
          >
            {t("landing.sites.recent_projects")}
          </Text>
        </Box>
        {sites && sites.length > 0 ? (
          <Box direction="vertical" width="100%" maxWidth={1480} align="center">
            <Box direction="horizontal" flexWrap="wrap" align="left">
              {sites.map((site) => (
                <SiteThumbnail key={site.id} site={site} />
              ))}
            </Box>
            {hasMore && (
              <Box marginTop={4} marginBottom={4}>
                <Button
                  priority="secondary"
                  onClick={loadMore}
                  disabled={isFetchingNextPage}
                  prefixIcon={
                    isFetchingNextPage ? (
                      <Box marginRight={1}>
                        <Loader size="tiny" />
                      </Box>
                    ) : undefined
                  }
                >
                  {isFetchingNextPage
                    ? t("landing.sites.loading_more")
                    : t("landing.sites.seeMore")}
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <NoSitesFound />
        )}
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box align="center" width="100%" verticalAlign="middle" height="100%">
        <Loader size="small" />
      </Box>
    );
  }

  if (sites && sites.length === 0 && !isLoading && !isFetchingNextPage) {
    return <NoSitesFound />;
  }

  return null;
};

const NoSitesFound = () => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  return (
    <Box
      marginTop={isMobile ? "24px" : "48px"}
      direction="vertical"
      align="center"
      textAlign="center"
      height="100%"
      width="calc(100% - 32px)"
      padding="0 16px"
    >
      <Text size="medium" weight="bold">
        {t("landing.sites.you_havent_created_sites_yet")}
      </Text>
      <Text size="tiny">
        {t("landing.sites.start_building_your_first_site")}
      </Text>
    </Box>
  );
};
